import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Loader2, CheckCircle, User, Mail, Heart, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const recommendSchema = z.object({
  yourName: z.string().trim().min(1, "Your name is required").max(100),
  yourEmail: z.string().trim().email("Please enter a valid email").max(255),
  friendName: z.string().trim().min(1, "Friend's name is required").max(100),
  friendEmail: z.string().trim().email("Please enter a valid friend's email").max(255),
  message: z.string().trim().max(500).optional(),
});

type RecommendFormData = z.infer<typeof recommendSchema>;

interface RecommendFriendDialogProps {
  children: React.ReactNode;
  defaultEmail?: string | null;
  defaultName?: string | null;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function RecommendFriendDialog({ children, defaultEmail, defaultName }: RecommendFriendDialogProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<RecommendFormData>({
    yourName: defaultName || "",
    yourEmail: defaultEmail || "",
    friendName: "",
    friendEmail: "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof RecommendFormData, string>>>({});

  const handleChange = (field: keyof RecommendFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = recommendSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof RecommendFormData, string>> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof RecommendFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: formData.yourName,
          email: formData.yourEmail,
          subject: `Friend Recommendation: ${formData.friendName}`,
          message: `Someone wants to recommend Shakatah to a friend!\n\n` +
            `From: ${formData.yourName} (${formData.yourEmail})\n\n` +
            `Friend's Name: ${formData.friendName}\n` +
            `Friend's Email: ${formData.friendEmail}\n\n` +
            `Personal Message:\n${formData.message || "No message provided"}`,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Thanks for spreading the love! 💜",
        description: "We'll reach out to your friend about Shakatah.",
      });
    } catch (err: any) {
      console.error("Error submitting recommendation:", err);
      toast({
        title: "Oops!",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      yourName: defaultName || "",
      yourEmail: defaultEmail || "",
      friendName: "",
      friendEmail: "",
      message: "",
    });
    setErrors({});
    setIsSubmitted(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) {
        setTimeout(resetForm, 300);
      }
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-customer" />
            Recommend to a Friend
          </DialogTitle>
          <DialogDescription>
            Know someone who'd love group gifting? Tell us about them!
          </DialogDescription>
        </DialogHeader>

        {isSubmitted ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-customer/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-customer" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Thanks for sharing! 💜
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              We'll reach out to {formData.friendName} and let them know about Shakatah.
            </p>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="email">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Your Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <User className="w-4 h-4" />
                    Your Details
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="yourName" className="text-xs">Your Name *</Label>
                      <Input
                        id="yourName"
                        placeholder="Your name"
                        value={formData.yourName}
                        onChange={(e) => handleChange("yourName", e.target.value)}
                        className={errors.yourName ? "border-destructive" : ""}
                      />
                      {errors.yourName && (
                        <p className="text-xs text-destructive">{errors.yourName}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="yourEmail" className="text-xs">Your Email *</Label>
                      <Input
                        id="yourEmail"
                        type="email"
                        placeholder="you@email.com"
                        value={formData.yourEmail}
                        onChange={(e) => handleChange("yourEmail", e.target.value)}
                        className={errors.yourEmail ? "border-destructive" : ""}
                      />
                      {errors.yourEmail && (
                        <p className="text-xs text-destructive">{errors.yourEmail}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Friend's Details */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Heart className="w-4 h-4" />
                    Friend's Details
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="friendName" className="text-xs">Friend's Name *</Label>
                      <Input
                        id="friendName"
                        placeholder="Friend's name"
                        value={formData.friendName}
                        onChange={(e) => handleChange("friendName", e.target.value)}
                        className={errors.friendName ? "border-destructive" : ""}
                      />
                      {errors.friendName && (
                        <p className="text-xs text-destructive">{errors.friendName}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="friendEmail" className="text-xs">Friend's Email *</Label>
                      <Input
                        id="friendEmail"
                        type="email"
                        placeholder="friend@email.com"
                        value={formData.friendEmail}
                        onChange={(e) => handleChange("friendEmail", e.target.value)}
                        className={errors.friendEmail ? "border-destructive" : ""}
                      />
                      {errors.friendEmail && (
                        <p className="text-xs text-destructive">{errors.friendEmail}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Personal Message */}
                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-xs">Add a personal note (optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us why you think they'd love Shakatah..."
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    rows={2}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-customer hover:bg-customer/90 text-customer-foreground"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2" />
                      Send Recommendation
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="whatsapp">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Share Shakatah with your friend directly on WhatsApp!
                </p>
                <Button 
                  className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
                  onClick={() => {
                    const message = encodeURIComponent(
                      `Hey! 👋 Check out Shakatah - it makes group gifting super easy! You can split payments with friends for gifts and purchases. Try it here: https://shakatahpro.lovable.app/my-purchases`
                    );
                    window.open(`https://wa.me/?text=${message}`, '_blank');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Share on WhatsApp
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Opens WhatsApp with a pre-filled message
                </p>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
